import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface QuickLink {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onPress: () => void;
}

interface DesktopQuickLinksTileProps {
  onMarketplacePress: () => void;
  onResourcesPress: () => void;
  onLinkedAccountsPress: () => void;
  onMessagesPress: () => void;
}

/**
 * DesktopQuickLinksTile - Quick navigation links tile for desktop home dashboard
 */
export const DesktopQuickLinksTile: React.FC<DesktopQuickLinksTileProps> = ({
  onMarketplacePress,
  onResourcesPress,
  onLinkedAccountsPress,
  onMessagesPress,
}) => {
  const quickLinks: QuickLink[] = [
    {
      id: 'marketplace',
      title: 'Marketplace',
      subtitle: 'Buy & sell with neighbors',
      icon: <RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS width={20} height={20} color={PRIMITIVE_COLORS.resioBlue} />,
      onPress: onMarketplacePress,
    },
    {
      id: 'resources',
      title: 'Resources',
      subtitle: 'Community amenities',
      icon: <RESIDENTS_SVG_COMPONENTS.WIDGETS width={20} height={20} color={PRIMITIVE_COLORS.resioPurple} />,
      onPress: onResourcesPress,
    },
    {
      id: 'linked-accounts',
      title: 'Linked Accounts',
      subtitle: 'Roommates & occupants',
      icon: <RESIDENTS_SVG_COMPONENTS.LINKED_ACCOUNTS width={20} height={20} color={PRIMITIVE_COLORS.successGreen} />,
      onPress: onLinkedAccountsPress,
    },
    {
      id: 'messages',
      title: 'Messages',
      subtitle: 'Property chat',
      icon: <RESIDENTS_SVG_COMPONENTS.CHAT_ICON width={20} height={20} color={PRIMITIVE_COLORS.warningOrange} />,
      onPress: onMessagesPress,
    },
  ];

  return (
    <View style={styles.tile}>
      <View style={styles.header}>
        <Text style={styles.title}>Quick Links</Text>
      </View>

      <View style={styles.linksGrid}>
        {quickLinks.map((link) => (
          <TouchableOpacity
            key={link.id}
            style={styles.linkItem}
            onPress={link.onPress}
            activeOpacity={0.7}
          >
            <View style={styles.linkIconContainer}>
              {link.icon}
            </View>
            <View style={styles.linkTextContainer}>
              <Text style={styles.linkTitle}>{link.title}</Text>
              <Text style={styles.linkSubtitle}>{link.subtitle}</Text>
            </View>
            <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color={SEMANTIC_COLORS.text.tertiary} />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  linksGrid: {
    gap: 4,
  },
  linkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    gap: 12,
  },
  linkIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkTextContainer: {
    flex: 1,
  },
  linkTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  linkSubtitle: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.tertiary,
    marginTop: 2,
  },
});

export default DesktopQuickLinksTile;
